// This is a COPY of downread/shared/src/schema/feed-schema.ts. To modify, edit the original and then run downread/bin/build-shared.sh
/**
 * File layout on servers and CDN: see /downread/FILE-LAYOUT.md
 */

/** Lowest value an admin may set the per-feed post-storage cap (maxStoredPosts) of a PUBLIC feed
 * to: an initial fetch stores up to MAX_FETCHED_ITEMS_INITIAL posts and clients track read state
 * for the last MAX_ACTIVE_POSTS, so public feeds keep at least this many. PRIVATE and NEWSLETTER
 * feeds are capped by the owner's plan instead (privateFeedMaxStoredPosts), which goes below this. */
export const MIN_STORED_POSTS = 1200;
/** Default post-storage cap for public feeds (effectively unlimited). */
export const DEFAULT_PUBLIC_MAX_STORED_POSTS = 1_000_000;
/** Fallback post-storage cap for private feeds whose creation path did not set one (the sync
 * writer's first-fetch backfill). Every current creation path sets the plan-derived cap instead. */
export const DEFAULT_PRIVATE_MAX_STORED_POSTS = MIN_STORED_POSTS;
/** A feed still on the public default cap but already past this many posts is flagged in the
 * admin UI so an admin can decide whether to keep storing it unbounded. */
export const STORED_POSTS_WARN_THRESHOLD = 10_000;

/** True for feed statuses that are personal/private and must be hidden from other users, kept off
 * public pages, encrypted on the CDN, and excluded from admin review. Covers both classic PRIVATE
 * feeds and email-fed NEWSLETTER feeds. Does NOT imply the feed is polled — newsletters never are. */
export const isPrivateLike = (feedStatus: string): boolean =>
    feedStatus == 'PRIVATE' || feedStatus == 'NEWSLETTER';

/**
 * Category types for RSS feeds
 */
export const FeedCategoryList = [
    'tech',
    'tech/ai',
    'tech/gadgets',
    'tech/startups',
    'tech/audio',
    'tech/video',
    'tech/mobile',
    'tech/tinkering',
    'tech/vintage',
    'tech/crypto',
    'tech/robotics',
    'news',
    'news/us',
    'news/uk',
    'news/aunz',
    'news/ca',
    'news/international',
    'news/local',
    'comics',
    'software-development',
    'software-development/ai',
    'software-development/web',
    'software-development/javascript',
    'software-development/java',
    'software-development/python',
    'software-development/mobile',
    'software-development/devops',
    'software-development/linux',
    'software-development/microsoft',
    'science',
    'space',
    'culture',
    'sports',
    'sports/football',
    'sports/basketball',
    'sports/baseball',
    'sports/soccer',
    'sports/other',
    'film+tv',
    'film+tv/reviews',
    'film+tv/news', 
    'comedy',
    'comedy/satire',
    'gaming',
    'gaming/pc',
    'gaming/consoles',
    'gaming/mobile',
    'gaming/board-games',
    'gaming/retro',
    'personal-blog',
    'crime',
    'business',
    'business/small',
    'business/finance',
    'business/investing',
    'business/industry-news',
    'music',
    'music/instruments',
    'music/pop',
    'music/rock',
    'music/classical',
    'music/country',
    'music/jazz',
    'music/rnb',
    'music/hip-hop',
    'music/electronic',
    'music/metal',
    'music/indie',
    'entertainment',
    'economics',
    'government',
    'defense',
    'hobby',
    'law',
    'fitness',
    'product-blog',
    'company-blog',
    'chat',
    'it',
    'it/security',
    'automotive',
    'aviation',
    'travel',
    'travel/cruising',
    'travel/destinations',
    'travel/tips',
    'travel/theme-parks',
    'travel/saving',
    'diy',
    'photography',
    'marketing',
    'marketing/advertising',
    'marketing/seo',
    'marketing/growth',
    'food',
    'food/drinks',
    'education',
    'fiction',
    'literature',
    'politics',
    'lifenhealth',
    'health',
    'parenting',
    'history',
    'art',
    'fashion',
    'environment',
    'nature',
    'religion',
    'astrology',
    'adult',
    'piracy+illegal'
] as const;

export type FeedCategory = typeof FeedCategoryList[number];

/**
 * Explanations for the few categories whose name alone is ambiguous. Sent to the categorization
 * LLM together with the category list.
 *
 * Deliberately sparse: every entry costs prompt tokens on every single feed analysis, so only add
 * one where the bare name has repeatedly been misread, and keep it to a short phrase.
 */
export const FeedCategoryDescriptions: Partial<Record<FeedCategory, string>> = {
    'personal-blog': "primarily about the author's own life or opinions rather than about a topic. Whenever the domain or title makes clear this is one individual's personal feed, this is the only correct category, even if they consistently write about a specific topic such as tech, cooking or finance",
    'product-blog': 'a blog dedicated to one particular product',
    'company-blog': 'a blog a company runs about itself: investor news, announcements, company culture',
    'politics': 'about politics',
    'government': 'published by a government agency or a similar public body',
};

/**
 * Lookup table mapping each category to all categories that start with it.
 * For example, 'tech' maps to ['tech', 'tech/gadgets'].
 */
export const CategoryWithSubcategories: Record<FeedCategory, FeedCategory[]> = FeedCategoryList.reduce(
    (acc, category) => {
        acc[category] = FeedCategoryList.filter(
            cat => cat === category || cat.startsWith(category + '/')
        );
        return acc;
    },
    {} as Record<FeedCategory, FeedCategory[]>
);

export const RssTypes = [
    'text/blog', 'audio/podcast', 'webcomic', 'video', 'links','mixed'
];

export type RssType = typeof RssTypes[number];

/**
 * Map of external script IDs to their URLs.
 * Used by the client to load the appropriate script when rendering posts.
 * This is the single source of truth for supported external scripts.
 */
export const ExternalScriptUrls = {
    'x-widget': 'https://platform.twitter.com/widgets.js',
    'tiktok-embed': 'https://www.tiktok.com/embed.js',
    'instagram-embed': 'https://www.instagram.com/embed.js',
    'threads-embed': 'https://www.threads.com/embed.js',
    'reddit-embed': 'https://embed.reddit.com/widgets.js',
    'bluesky-embed': 'https://embed.bsky.app/static/embed.js'
} as const;

export type ExternalScript = keyof typeof ExternalScriptUrls;
export const ExternalScriptTypes = Object.keys(ExternalScriptUrls) as ExternalScript[];

/** itunes:episodeType — the kind of episode. */
export type PodcastEpisodeType = 'full' | 'trailer' | 'bonus';

/** itunes:type — how a show's episodes are meant to be presented. */
export type PodcastShowType = 'episodic' | 'serial';

/** itunes:owner — the podcast's administrative contact (itunes:name + itunes:email). */
export interface PodcastOwner {
    name?: string;
    email?: string;
}

/**
 * Podcast-specific metadata from the iTunes namespace, attached to a Post when the
 * feed is a podcast. All fields are optional — feeds populate whichever tags they provide.
 *
 * Note: itunes:type, itunes:author and itunes:owner are channel-level in the spec; they
 * are surfaced per-episode here for convenience so a Post carries its full podcast context.
 */
export interface PodcastEpisode {
    /** itunes:explicit — whether the episode contains explicit content. */
    explicit?: boolean;
    /** itunes:duration — episode length in seconds (HH:MM:SS forms normalized to seconds). */
    duration?: number;
    /** itunes:episode — episode number within the season/show. */
    episode?: number;
    /** itunes:episodeType — full, trailer, or bonus. */
    episodeType?: PodcastEpisodeType;
    /** itunes:type — episodic or serial (channel-level in the spec). */
    type?: PodcastShowType;
    /** itunes:author — episode/show author (channel-level in the spec). */
    author?: string;
    /** itunes:owner — administrative contact (channel-level in the spec). */
    owner?: PodcastOwner;
}

/**
 * YouTube-specific metadata from the media:/yt: namespaces, attached to a Post when the
 * feed is a YouTube channel/playlist Atom feed. Lets the client render an embedded player
 * and show engagement stats. All fields except videoId are optional.
 */
export interface YoutubeVideo {
    /** yt:videoId — the video id used to build the embed/watch URL. */
    videoId: string;
    /** yt:channelId — the owning channel. */
    channelId?: string;
    /** media:statistics views at the time the post was stored. */
    views?: number;
    /** media:starRating count (number of ratings). */
    ratingCount?: number;
    /** media:starRating average, 0–5. */
    ratingAverage?: number;
}

/**
 * Comment info for a post, merged from two sources: the plain RSS <comments> element (a
 * human-readable comments page) and <source:comments count feedUrl/> from the 'source'
 * namespace (https://source.scripting.com/), used by rss.chat and other social-RSS systems.
 * All fields are optional — the object is only present when at least one of them is set.
 *
 * Note: `count` is a snapshot taken when the post was last written. Comment counts alone do
 * not change a post's content hash, so a post is not rewritten just because it gained comments.
 */
export interface PostComments {
    /** source:comments @count — number of comments on the post when it was last stored. */
    count?: number;
    /** source:comments @feedUrl — RSS feed whose items are the comments on this post. */
    feedUrl?: string;
    /** RSS <comments> — human-readable comments page for the post. */
    htmlUrl?: string;
}

/**
 * source:inReplyTo — identifies the item this post is a reply to. Per the spec the value is a
 * permalink URL unless the element carries isPermaLink="false", in which case it is an opaque
 * id (e.g. a Bluesky DID), following the RSS 2.0 guid model.
 */
export interface PostInReplyTo {
    /** The permalink URL, or the opaque id when isPermalink is false. */
    value: string;
    /** True when `value` is a permalink URL, false when it is an opaque id. */
    isPermalink: boolean;
}

/**
 * source:account — a social account of the feed's author, from the channel-level
 * <source:account service="mastodon">@davew@mastodon.social</source:account> element.
 * A feed may declare one per service.
 */
export interface FeedAccount {
    /** The `service` attribute, lowercased (the spec declares service names case-insensitive). */
    service: string;
    /** The element value: the author's username/screenname on that service. */
    name: string;
}

/**
 * Represents a post/article from an RSS feed.
 * This will be stored in object storage, not in the database.
 */
export interface Post {
    /** Sequential post number within the feed (assigned by downread) */
    number: number;
    /** Title of the post */
    title?: string;
    /** URL link to the full post */
    link?: string;
    /** HTML description or content of the post */
    description?: string;
    /** Author name or email */
    author?: string;
    /** Comments on the post: RSS <comments> page plus source:comments count/feed.
     *  Archives written before this field became structured hold a bare URL string here. */
    comments?: PostComments;
    /** Publication date as provided by the feed (ISO 8601 string). This is the date shown to users. */
    pubDate?: string;
    /** Normalized publication date in 10-second precision (epoch ms / 10000), adjusted if pub date not believable.
     * DO NOT SHOW THIS DIRECTLY TO USERS. For sorting only.
     */
    semiFeedPubTimeMs?: number;
    /** URL of media attachment (audio, video, etc.) from enclosure */
    mediaUrl?: string;
    /** MIME type of media attachment (e.g., "audio/mpeg", "video/mp4") from enclosure */
    mediaType?: string;
    /** URL of an image associated with the post (ideally ~200x100px) */
    imageUrl?: string;
    /** URL of a smaller thumbnail version of the post image */
    thumbnailUrl?: string;
    /** Hash of post content (title + description + enclosure) for detecting modifications */
    contentHash: string;
    /** True if description was truncated due to length limits */
    descriptionTruncated?: boolean;
    /** External scripts detected in the content (e.g., Twitter widgets, TikTok embeds) */
    externalScripts?: ExternalScript[];
    /** Podcast (iTunes namespace) metadata — present only for podcast feed items. */
    podcastEpisode?: PodcastEpisode;
    /** YouTube (media:/yt: namespace) metadata — present only for YouTube feed items. */
    youtube?: YoutubeVideo;
    /** source:inReplyTo — the post this one replies to. Present only for social-RSS feeds. */
    inReplyTo?: PostInReplyTo;
    /** Approximate stored size of the post in bytes, carried over from FeedParserItem.size: the
     * feed-reader computes it (shared/utils/post-size.ts feedItemByteSize) once the item is fully
     * sanitized — the UTF-8 length of the body, title, author and every URL, plus
     * POST_SIZE_OVERHEAD for dates and the like. Summed per feed to enforce the per-plan storage
     * limits (maxInboxStorageMb / maxPrivateFeedStorageMb). Absent on posts written before this
     * field existed; those count as 0. */
    size?: number;
}

/** Bytes added to every post's size for dates, numbers, flags and JSON structure. */
export const POST_SIZE_OVERHEAD = 64;

/**
 * Approximate per-post averages of a feed's content, maintained by the sync writer
 * (sync-server/lib/post-stats.ts). Computed over the whole fetch the first time, then blended
 * with the average of each fetch's new posts, so the values follow the feed's recent output.
 * Rounded to two decimals; informational only, never sorted or filtered on.
 */
export interface PostStats {
    /** Words in the post body (HTML stripped). */
    words: number;
    /** Images: <img> tags in the body plus attached/enclosure images. */
    images: number;
    /** Videos: <video> tags, video-player iframes/embeds, video enclosures, YouTube items. */
    videos: number;
    /** Audio: <audio> tags, podcast-player iframes, audio enclosures, podcast episodes. */
    audios: number;
}

/**
 * Metadata for tracking posts without storing full content.
 * Used to detect new vs modified posts in subsequent feed fetches.
 */
export interface PostMeta {
    /** Publication date as provided by the feed (ISO 8601 string) */
    pubDate?: string;
    /** Globally unique identifier from the feed */
    guid?: string;
    /** URL link to the post */
    link?: string;
    /** Original/external link, when the post links out (RSS feedburner origLink, JSON Feed
     *  external_url). Kept for cross-feed dedup: "linked list" blogs (e.g. Daring Fireball)
     *  expose the external URL as the RSS <link> but the permalink as the JSON Feed url, so
     *  the RSS link only matches the JSON feed's origlink. Omitted when absent. */
    origlink?: string;
}

/**
 * Collection of posts from a feed stored in object storage.
 * Posts are grouped into collections for efficient storage and retrieval.
 * Each collection contains a contiguous range of posts by sequence number.
 */
export interface FeedCollection {
    /** Feed ID (downread's internal identifier) */
    id: string;
    /** Sequence number of the first post in this collection */
    firstPost: number;
    /** Sequence number of the last post in this collection */
    lastPost: number;
    /** Timestamp when this collection was written (ISO 8601 string, server time) */
    written: string;
    /** Array of posts in this collection */
    posts: Post[];
}

/** Post in the random feed — extends Post with source feed metadata for rendering */
export interface RandomPost extends Post {
    /** ID of the feed this post came from */
    feedId: string;
    /** Display name of the source feed */
    feedName: string;
    /** Icon URL of the source feed (favicon or feed image) */
    feedIconUrl?: string;
    /** Post number in the source feed (used for share links). `number` holds the random-feed index. */
    feedPostNumber: number;
}
